import React, { useState, useEffect } from 'react';
import { userAPI, companyAPI } from '../../services/api';
import UserModal from './UserModal';
import '../../styles/admin/UserManagement.css';

/**
 * ユーザー管理セクション
 */
const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // ユーザー一覧を取得
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await userAPI.getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('ユーザー一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 企業一覧を取得
  const fetchCompanies = async () => {
    try {
      const data = await companyAPI.getCompanies();
      setCompanies(data);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, []);

  // 企業名を取得
  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.company_id === companyId);
    return company ? company.company_name : `ID: ${companyId}`;
  };

  // ロール名を日本語に変換
  const getRoleName = (role) => {
    const roleNames = {
      'system_admin': 'システム管理者',
      'company_admin': '企業管理者',
      'general': '一般ユーザー'
    };
    return roleNames[role] || role;
  };

  // 新規作成モーダルを開く
  const handleCreate = () => {
    setEditingUser(null);
    setModalOpen(true);
  };

  // 編集モーダルを開く
  const handleEdit = (user) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  // ユーザー削除
  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`「${userName}」を削除してもよろしいですか？`)) {
      return;
    }

    try {
      await userAPI.deleteUser(userId);
      alert('ユーザーを削除しました');
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert(err.response?.data?.detail || 'ユーザーの削除に失敗しました');
    }
  };

  // 招待メール再送信
  const handleResendInvitation = async (userId, email) => {
    if (!window.confirm(`「${email}」に招待メールを再送信しますか？`)) {
      return;
    }

    try {
      await userAPI.resendInvitation(userId);
      alert('招待メールを再送信しました');
      fetchUsers();
    } catch (err) {
      console.error('Failed to resend invitation:', err);
      alert(err.response?.data?.detail || '招待メールの再送信に失敗しました');
    }
  };

  // モーダルを閉じる
  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingUser(null);
  };

  // モーダルから保存成功
  const handleSaveSuccess = () => {
    handleCloseModal();
    fetchUsers();
  };

  // ソート処理
  const handleSort = (field) => {
    if (sortField === field) {
      // 同じフィールドをクリックした場合は昇順/降順を切り替え
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // 新しいフィールドの場合は昇順でソート
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // キーボードでソート処理
  const handleSortKeyDown = (e, field) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSort(field);
    }
  };

  // aria-sort属性を取得
  const getAriaSort = (field) => {
    if (sortField !== field) return 'none';
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  };

  // ソート済みユーザーリストを取得
  const getSortedUsers = () => {
    if (!sortField) return users;

    const sorted = [...users].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // 所属企業でソートする場合は企業名を取得
      if (sortField === 'company_id') {
        aValue = getCompanyName(a.company_id);
        bValue = getCompanyName(b.company_id);
      }

      // 文字列として比較
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  return (
    <div className="user-management">
      <div className="section-header">
        <h2>ユーザー管理</h2>
        <button onClick={handleCreate} className="btn-primary">
          + 新規ユーザーを追加
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="loading" role="status" aria-live="polite">読み込み中...</div>
      ) : (
        <div className="user-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort('display_name')}
                  onKeyDown={(e) => handleSortKeyDown(e, 'display_name')}
                  tabIndex={0}
                  aria-sort={getAriaSort('display_name')}
                  style={{ cursor: 'pointer' }}
                >
                  名前 {sortField === 'display_name' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th
                  onClick={() => handleSort('email')}
                  onKeyDown={(e) => handleSortKeyDown(e, 'email')}
                  tabIndex={0}
                  aria-sort={getAriaSort('email')}
                  style={{ cursor: 'pointer' }}
                >
                  メールアドレス {sortField === 'email' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th
                  onClick={() => handleSort('company_id')}
                  onKeyDown={(e) => handleSortKeyDown(e, 'company_id')}
                  tabIndex={0}
                  aria-sort={getAriaSort('company_id')}
                  style={{ cursor: 'pointer' }}
                >
                  所属企業 {sortField === 'company_id' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th
                  onClick={() => handleSort('role')}
                  onKeyDown={(e) => handleSortKeyDown(e, 'role')}
                  tabIndex={0}
                  aria-sort={getAriaSort('role')}
                  style={{ cursor: 'pointer' }}
                >
                  権限 {sortField === 'role' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th
                  onClick={() => handleSort('email_verified')}
                  onKeyDown={(e) => handleSortKeyDown(e, 'email_verified')}
                  tabIndex={0}
                  aria-sort={getAriaSort('email_verified')}
                  style={{ cursor: 'pointer' }}
                >
                  ステータス {sortField === 'email_verified' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th
                  onClick={() => handleSort('created_at')}
                  onKeyDown={(e) => handleSortKeyDown(e, 'created_at')}
                  tabIndex={0}
                  aria-sort={getAriaSort('created_at')}
                  style={{ cursor: 'pointer' }}
                >
                  作成日時 {sortField === 'created_at' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    ユーザーが登録されていません
                  </td>
                </tr>
              ) : (
                getSortedUsers().map((user) => (
                  <tr key={user.user_id}>
                    <td>{user.display_name}</td>
                    <td>{user.email}</td>
                    <td>{getCompanyName(user.company_id)}</td>
                    <td>
                      <span className={`role-badge role-${user.role}`}>
                        {getRoleName(user.role)}
                      </span>
                    </td>
                    <td>
                      {user.email_verified ? (
                        <span className="status-badge status-verified">
                          ✓ 確認済み
                        </span>
                      ) : (
                        <span className="status-badge status-unverified">
                          ⚠ 未確認
                        </span>
                      )}
                    </td>
                    <td>{new Date(user.created_at).toLocaleString('ja-JP')}</td>
                    <td className="action-buttons">
                      {!user.email_verified && (
                        <button
                          onClick={() => handleResendInvitation(user.user_id, user.email)}
                          className="btn-resend"
                          title="招待メールを再送信"
                        >
                          再送信
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(user)}
                        className="btn-edit"
                      >
                        編集
                      </button>
                      <button
                        onClick={() => handleDelete(user.user_id, user.display_name)}
                        className="btn-delete"
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ユーザー作成・編集モーダル */}
      {modalOpen && (
        <UserModal
          user={editingUser}
          companies={companies}
          onClose={handleCloseModal}
          onSaveSuccess={handleSaveSuccess}
        />
      )}
    </div>
  );
};

export default UserManagement;
